import { getRegionalFormat } from '@dynatrace-sdk/user-preferences';

type LocaleSeparators = {
  decimal: string;
  group: string | null;
};
// \u00A0 / \u202F escapes — left intentionally as-is; they're invisible whitespace
type GroupSeparator = ',' | '.' | ' ' | '\u00A0' | '\u202F' | "'";

/**
 * Parses a value into a JavaScript number (Replace commas with dots).
 * @internal
 */
export function parseNumber(value: string | number): number {
  if (typeof value === 'number') {
    return value;
  }
  const regionalFormat = getRegionalFormat();
  const { decimal: localeDecimalSeparator } =
    getLocaleSeparators(regionalFormat);
  const normalizedValue = checkFormat(value);
  const decimalNormalizedValue = normalizedValue.replaceAll(
    localeDecimalSeparator,
    '.',
  );
  const groupingNormalizedValue = decimalNormalizedValue.replace(
    // Match whitespace-like grouping separators (space, NBSP, narrow NBSP, apostrophe), e.g. "1 234", "1\u00A0234", "1\u202F234", "1'234".
    /[\s\u00A0\u202F']/g,
    '',
  );

  return parseFloat(groupingNormalizedValue);
}

/**
 * Removes wrappers and unsupported characters from a number-like input.
 */
function sanitizeNumberLikeInput(inputValue: string): string {
  let sanitizedValue = inputValue.trim();

  // Match values wrapped in parentheses, e.g. "(123.45)", "( 1 234,56 )".
  if (/^\(.*\)$/.exec(sanitizedValue)) {
    sanitizedValue = sanitizedValue.slice(1, -1).trim();
  }

  // Match any plus/minus sign anywhere in the input, e.g. "+12", "1-2".
  const containsSignCharacter = /[-+]/.exec(sanitizedValue);
  if (containsSignCharacter) {
    // Remove every plus/minus sign globally, e.g. "+-12" -> "12".
    sanitizedValue = sanitizedValue.replace(/[-+]/g, '').trim();
  }

  // Keep only digits, dot, comma, apostrophe, and whitespace-style separators, e.g. "€1,234.50" -> "1,234.50".
  return sanitizedValue.replace(/[^\d.,'\s\u00A0\u202F]/g, '');
}

/**
 * Counts digits on each side of a separator index.
 */
function countDigitsAroundSeparator(
  inputValue: string,
  separatorIndex: number,
): { leftPart: string; leftDigitCount: number; rightDigitCount: number } {
  const leftPart = inputValue.slice(0, separatorIndex);
  const rightPart = inputValue.slice(separatorIndex + 1);
  // Match each digit character on the left side, e.g. "ab12" -> 2 matches.
  const leftDigitCount = (leftPart.match(/\d/g) || []).length;
  // Match each digit character on the right side, e.g. "3x45" -> 3 matches.
  const rightDigitCount = (rightPart.match(/\d/g) || []).length;

  return { leftPart, leftDigitCount, rightDigitCount };
}

/**
 * Checks whether the left side follows a grouping pattern such as 1,234,567.
 */
function hasLeftGroupingPattern(
  leftPart: string,
  separatorCharacter: ',' | '.',
): boolean {
  // Match whitespace-like grouping separators and remove them first, e.g. "1 234" -> "1234".
  const cleanedLeftPart = leftPart.replace(/[\s\u00A0\u202F']/g, '');
  const groupedParts = cleanedLeftPart.split(separatorCharacter);

  if (groupedParts.length <= 1) {
    return false;
  }

  // Match 1 to 3 digits for the first group, e.g. "1", "12", "123".
  if (!/^\d{1,3}$/.exec(groupedParts[0])) {
    return false;
  }

  for (let partIndex = 1; partIndex < groupedParts.length; partIndex++) {
    // Match exactly 3 digits for each following group, e.g. in "1,234,567" both "234" and "567".
    if (!/^\d{3}$/.exec(groupedParts[partIndex])) {
      return false;
    }
  }

  return true;
}

/**
 * Detects a whitespace-like grouping separator from the input.
 */
function detectWhitespaceGroupingSeparator(
  inputValue: string,
): GroupSeparator | null {
  // Match any supported whitespace-like grouping separator, e.g. space, NBSP, narrow NBSP, apostrophe.
  if (!/[ \u00A0\u202F']/.exec(inputValue)) {
    return null;
  }

  if (inputValue.includes('\u202F')) {
    return '\u202F';
  }

  if (inputValue.includes('\u00A0')) {
    return '\u00A0';
  }

  if (inputValue.includes("'")) {
    return "'";
  }

  return ' ';
}

/**
 * Infers decimal and grouping separators from comma and dot occurrences.
 */
function inferDotAndCommaSeparators(
  sanitizedInputValue: string,
  fallbackDecimalSeparator: string,
): LocaleSeparators {
  const lastCommaIndex = sanitizedInputValue.lastIndexOf(',');
  const lastDotIndex = sanitizedInputValue.lastIndexOf('.');

  let decimalSeparator = fallbackDecimalSeparator;
  let groupSeparator: GroupSeparator | null = null;

  if (lastCommaIndex !== -1 && lastDotIndex !== -1) {
    decimalSeparator = lastCommaIndex > lastDotIndex ? ',' : '.';
    groupSeparator = decimalSeparator === ',' ? '.' : ',';
    return { decimal: decimalSeparator, group: groupSeparator };
  }

  if (lastCommaIndex === -1 && lastDotIndex === -1) {
    return { decimal: decimalSeparator, group: groupSeparator };
  }

  const separatorCharacter =
    lastCommaIndex !== -1 ? (',' as const) : ('.' as const);
  const separatorIndex = lastCommaIndex !== -1 ? lastCommaIndex : lastDotIndex;
  const { leftPart, rightDigitCount } = countDigitsAroundSeparator(
    sanitizedInputValue,
    separatorIndex,
  );

  const leftPartUsesGrouping = hasLeftGroupingPattern(
    leftPart,
    separatorCharacter,
  );
  const hasExactlyThreeDigitsOnRight = rightDigitCount === 3;

  if (
    hasExactlyThreeDigitsOnRight &&
    (leftPartUsesGrouping || separatorCharacter !== fallbackDecimalSeparator)
  ) {
    groupSeparator = separatorCharacter;
  } else {
    decimalSeparator = separatorCharacter;
  }

  return { decimal: decimalSeparator, group: groupSeparator };
}

/**
 * Infers decimal and grouping separators from an input string.
 */
function inferSeparators(
  inputValue: string | null,
  fallbackDecimalSeparator: string,
): LocaleSeparators {
  if (inputValue === null) {
    return { decimal: fallbackDecimalSeparator, group: null };
  }

  const sanitizedInputValue = sanitizeNumberLikeInput(inputValue);
  const inferredDotAndCommaSeparators = inferDotAndCommaSeparators(
    sanitizedInputValue,
    fallbackDecimalSeparator,
  );

  const whitespaceGroupingSeparator =
    detectWhitespaceGroupingSeparator(sanitizedInputValue);
  if (whitespaceGroupingSeparator) {
    return {
      decimal: inferredDotAndCommaSeparators.decimal,
      group: whitespaceGroupingSeparator,
    };
  }

  if (
    !inferredDotAndCommaSeparators.group &&
    (sanitizedInputValue.includes(',') || sanitizedInputValue.includes('.'))
  ) {
    const alternateSeparator: ',' | '.' =
      inferredDotAndCommaSeparators.decimal === ',' ? '.' : ',';

    if (sanitizedInputValue.includes(alternateSeparator)) {
      return {
        decimal: inferredDotAndCommaSeparators.decimal,
        group: alternateSeparator,
      };
    }
  }

  return inferredDotAndCommaSeparators;
}

/**
 * Resolves decimal and grouping separators for a given locale.
 * @internal
 */
export function getLocaleSeparators(
  locale: string,
  options: Intl.NumberFormatOptions = {},
): LocaleSeparators {
  const numberFormatter = new Intl.NumberFormat(locale, {
    useGrouping: true,
    ...options,
  });
  const formattedParts = numberFormatter.formatToParts(1234.5);

  let decimalSeparator = '.';
  let groupSeparator: string | null = null;

  for (const part of formattedParts) {
    if (part.type === 'decimal') {
      decimalSeparator = part.value;
    }
    if (part.type === 'group') {
      groupSeparator = part.value;
    }
  }

  return { decimal: decimalSeparator, group: groupSeparator };
}

/**
 * Removes all occurrences of a token except for the last one.
 */
function removeAllButLast(sourceText: string, tokenToKeepLast: string): string {
  const lastTokenIndex = sourceText.lastIndexOf(tokenToKeepLast);
  if (lastTokenIndex === -1) {
    return sourceText;
  }

  const prefix = sourceText.slice(0, lastTokenIndex);
  const suffix = sourceText.slice(lastTokenIndex);

  return prefix.replaceAll(tokenToKeepLast, '') + suffix;
}

/**
 * Normalizes numeric text to locale separators and removes grouping separators.
 * @internal
 */
export function checkFormat(value: string): string {
  const regionalFormat = getRegionalFormat();
  const { decimal } = getLocaleSeparators(regionalFormat);
  const { decimal: inferredDecimal, group: inferredGroup } = inferSeparators(
    value,
    decimal,
  );

  let normalizedValue = value;

  if (inferredGroup) {
    normalizedValue = normalizedValue.replaceAll(inferredGroup, '');
  }

  if (inferredDecimal && decimal) {
    normalizedValue = normalizedValue.replaceAll(
      inferredDecimal ?? '',
      decimal,
    );
    normalizedValue = removeAllButLast(normalizedValue, decimal);
  }

  return normalizedValue;
}
